import dayjs from './date.js';
import { MAP_COLORS } from './MapUtil.jsx';
import { calendarForActivity, msToYmd, paintsDay } from './workCalendar.js';

/**
 * Weeks are Monday–Sunday everywhere (HITO). dayjs weekStart is 1.
 */

export const STATUS_COLORS = {
	complete: '#337ab7',
	critical: '#d9534f',
	normal: '#5cb85c',
	unassigned: '#999999'
};

export const COLOR_BY = {
	STATUS: 'status',
	CODE: 'code',
	RESOURCE: 'resource'
};

export const RANGE = {
	MONTH: 'month',
	WEEK: 'week'
};

export const parseRgb = color => {
	if (!color) {
		return null;
	}
	const text = String(color).trim();
	const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(text);
	if (hex) {
		let h = hex[1];
		if (h.length === 3) {
			h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
		}
		return {
			r: parseInt(h.slice(0, 2), 16),
			g: parseInt(h.slice(2, 4), 16),
			b: parseInt(h.slice(4, 6), 16)
		};
	}
	const rgb = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i.exec(text);
	if (!rgb) {
		return null;
	}
	return {
		r: Number(rgb[1]),
		g: Number(rgb[2]),
		b: Number(rgb[3])
	};
};

/** Black or white chip text from YIQ brightness (white on dark fills, black on light). */
export const chipTextColor = background => {
	const rgb = parseRgb(background);
	if (!rgb) {
		return '#111111';
	}
	const yiq = ((rgb.r * 299) + (rgb.g * 587) + (rgb.b * 114)) / 1000;
	return yiq >= 128 ? '#111111' : '#ffffff';
};

const MS_PER_DAY = 86400000;

export const startOfDayMs = ms => {
	const n = Number(ms) || 0;
	if (!n) {
		return 0;
	}
	return dayjs(n).startOf('day').valueOf();
};

export const buildMonthGrid = anchorMs => {
	const monthStart = dayjs(anchorMs || Date.now()).startOf('month');
	const gridStart = monthStart.startOf('week');
	const days = [];
	for (let i = 0; i < 42; i++) {
		const d = gridStart.add(i, 'day');
		days.push({
			key: d.format('YYYY-MM-DD'),
			ms: d.valueOf(),
			date: d.date(),
			inMonth: d.month() === monthStart.month(),
			isToday: d.isSame(dayjs(), 'day')
		});
	}
	return {
		year: monthStart.year(),
		month: monthStart.month(),
		monthLabel: monthStart.format('MMMM YYYY'),
		monthStartMs: monthStart.valueOf(),
		monthEndMs: monthStart.endOf('month').valueOf(),
		days
	};
};

export const buildWeekGrid = anchorMs => {
	const anchor = dayjs(anchorMs || Date.now()).startOf('day');
	const weekStart = anchor.startOf('week');
	const weekEnd = weekStart.add(6, 'day');
	const days = [];
	for (let i = 0; i < 7; i++) {
		const d = weekStart.add(i, 'day');
		days.push({
			key: d.format('YYYY-MM-DD'),
			ms: d.valueOf(),
			date: d.date(),
			inMonth: true,
			isToday: d.isSame(dayjs(), 'day')
		});
	}
	let monthLabel;
	if (weekStart.month() === weekEnd.month() && weekStart.year() === weekEnd.year()) {
		monthLabel = `${weekStart.format('D')} – ${weekEnd.format('D MMM YYYY')}`;
	} else if (weekStart.year() === weekEnd.year()) {
		monthLabel = `${weekStart.format('D MMM')} – ${weekEnd.format('D MMM YYYY')}`;
	} else {
		monthLabel = `${weekStart.format('D MMM YYYY')} – ${weekEnd.format('D MMM YYYY')}`;
	}
	return {
		year: weekStart.year(),
		month: weekStart.month(),
		monthLabel,
		monthStartMs: weekStart.valueOf(),
		monthEndMs: weekEnd.endOf('day').valueOf(),
		days
	};
};

export const isMilestone = activity => {
	return activity && (activity.taskType === 'TT_Mile' || activity.taskType === 'TT_FinMile');
};

export const activityOverlapsDay = (activity, dayMs, dataset) => {
	if (!activity) {
		return false;
	}
	const day0 = startOfDayMs(dayMs);
	if (!day0) {
		return false;
	}
	const day1 = day0 + MS_PER_DAY - 1;
	if (isMilestone(activity)) {
		const pin = activity.taskType === 'TT_FinMile'
			? startOfDayMs(activity.finish) || startOfDayMs(activity.start)
			: startOfDayMs(activity.start) || startOfDayMs(activity.finish);
		return pin === day0;
	}
	const start = Number(activity.start) || 0;
	const finish = Number(activity.finish) || start;
	if (!start) {
		return false;
	}
	if (start > day1 || finish < day0) {
		return false;
	}
	if (dataset) {
		const cal = calendarForActivity(activity, dataset);
		return paintsDay(msToYmd(day0), start, finish, cal);
	}
	return startOfDayMs(start) <= day1 && startOfDayMs(finish) >= day0;
};

export const activityCodeSeqNum = (activity, codeTypeId, datasetInfo) => {
	if (!activity || !codeTypeId) {
		return Number.MAX_SAFE_INTEGER;
	}
	const prop = activity.properties && (
		activity.properties[`_${codeTypeId}`]
		|| activity.properties[`_${String(codeTypeId)}`]
	);
	if (prop && prop.seq != null && prop.seq !== '') {
		const seq = Number(prop.seq);
		if (!Number.isNaN(seq)) {
			return seq;
		}
	}
	const type = datasetInfo && datasetInfo.activityCodeTypes
		? (datasetInfo.activityCodeTypes[codeTypeId] || datasetInfo.activityCodeTypes[String(codeTypeId)])
		: null;
	const codeId = prop && prop.id != null ? prop.id : null;
	if (type && codeId != null && type.valuesSeq && type.valuesSeq[codeId] != null) {
		return Number(type.valuesSeq[codeId]);
	}
	if (type && codeId != null && type.valuesIndex && type.valuesIndex[codeId] != null) {
		return Number(type.valuesIndex[codeId]);
	}
	return Number.MAX_SAFE_INTEGER;
};

export const activitiesForDay = (activities, dayMs, sortOptions) => {
	if (!Array.isArray(activities) || !activities.length) {
		return [];
	}
	const list = activities.filter(activity => activityOverlapsDay(activity, dayMs, sortOptions && sortOptions.datasetInfo));
	const codeTypeId = sortOptions && sortOptions.codeTypeId;
	if (!codeTypeId) {
		return list;
	}
	const datasetInfo = sortOptions.datasetInfo;
	return list.slice().sort((a, b) => {
		const seqA = activityCodeSeqNum(a, codeTypeId, datasetInfo);
		const seqB = activityCodeSeqNum(b, codeTypeId, datasetInfo);
		if (seqA !== seqB) {
			return seqA - seqB;
		}
		const nameCmp = String(a.name || '').localeCompare(String(b.name || ''));
		if (nameCmp !== 0) {
			return nameCmp;
		}
		return String(a.taskCode || '').localeCompare(String(b.taskCode || ''));
	});
};

const hashIndex = value => {
	const text = String(value || '');
	let hash = 0;
	for (let i = 0; i < text.length; i++) {
		hash = ((hash << 5) - hash) + text.charCodeAt(i);
		hash |= 0;
	}
	return Math.abs(hash) % MAP_COLORS.length;
};

export const activityStatusColor = (activity, datasetInfo) => {
	if (!activity) {
		return STATUS_COLORS.unassigned;
	}
	if (activity.statusCode === 'TK_Complete') {
		return STATUS_COLORS.complete;
	}
	const projects = datasetInfo && datasetInfo.projects && !Array.isArray(datasetInfo.projects)
		? datasetInfo.projects
		: null;
	const projectMeta = (projects && activity.projId && projects[activity.projId]) || {};
	const criticalThreshold = Number(projectMeta.criticalDrtnHrCnt) || 0;
	const isCritical = activity.drivingPathFlag === 'Y' || activity.totalFloat < criticalThreshold;
	return isCritical ? STATUS_COLORS.critical : STATUS_COLORS.normal;
};

export const activityCodeColor = (activity, codeTypeId, datasetInfo) => {
	if (!activity || !codeTypeId || !datasetInfo || !datasetInfo.activityCodeTypes) {
		return STATUS_COLORS.unassigned;
	}
	const type = datasetInfo.activityCodeTypes[codeTypeId]
		|| datasetInfo.activityCodeTypes[String(codeTypeId)];
	const prop = activity.properties && (
		activity.properties[`_${codeTypeId}`]
		|| activity.properties[`_${String(codeTypeId)}`]
	);
	if (!type || !prop || prop.id == null) {
		return STATUS_COLORS.unassigned;
	}
	const index = type.valuesIndex
		? type.valuesIndex[prop.id]
		: (type.valuesSequence || []).indexOf(prop.id);
	if (index == null || index < 0) {
		return STATUS_COLORS.unassigned;
	}
	return MAP_COLORS[index % MAP_COLORS.length];
};

export const activityResourceColor = activity => {
	const name = activity && Array.isArray(activity.resources) && activity.resources.length
		? activity.resources[0]
		: '';
	if (!name) {
		return STATUS_COLORS.unassigned;
	}
	return MAP_COLORS[hashIndex(name)];
};

export const activityChipColor = (activity, colorBy, codeTypeId, datasetInfo) => {
	if (colorBy === COLOR_BY.CODE) {
		return activityCodeColor(activity, codeTypeId, datasetInfo);
	}
	if (colorBy === COLOR_BY.RESOURCE) {
		return activityResourceColor(activity);
	}
	return activityStatusColor(activity, datasetInfo);
};

export const legendItems = (colorBy, codeTypeId, datasetInfo, activities) => {
	if (colorBy === COLOR_BY.CODE && codeTypeId && datasetInfo && datasetInfo.activityCodeTypes) {
		const type = datasetInfo.activityCodeTypes[codeTypeId]
			|| datasetInfo.activityCodeTypes[String(codeTypeId)];
		if (!type || !type.valuesSequence) {
			return [];
		}
		return type.valuesSequence.map((id, index) => ({
			key: String(id),
			label: type.values[id] || String(id),
			color: MAP_COLORS[index % MAP_COLORS.length]
		}));
	}
	if (colorBy === COLOR_BY.RESOURCE) {
		const seen = {};
		const items = [];
		(activities || []).forEach(activity => {
			const name = Array.isArray(activity.resources) && activity.resources.length
				? activity.resources[0]
				: 'Unassigned';
			if (name in seen) {
				return;
			}
			seen[name] = true;
			items.push({
				key: name,
				label: name,
				color: name === 'Unassigned' ? STATUS_COLORS.unassigned : MAP_COLORS[hashIndex(name)]
			});
		});
		items.sort((a, b) => a.label.localeCompare(b.label));
		return items;
	}
	return [
		{ key: 'complete', label: 'Complete', color: STATUS_COLORS.complete },
		{ key: 'critical', label: 'Critical', color: STATUS_COLORS.critical },
		{ key: 'normal', label: 'Non-critical', color: STATUS_COLORS.normal }
	];
};

export const summarizeProjectFilter = projectFilter => {
	if (!projectFilter) {
		return 'No filters';
	}
	const parts = [];
	if (projectFilter.searchText) {
		parts.push(`search "${projectFilter.searchText}"`);
	}
	const statusCount = Object.keys(projectFilter.activityStatus || {}).length;
	if (statusCount) {
		parts.push(`${statusCount} status`);
	}
	let codeCount = 0;
	for (const typeId in projectFilter.activityCodes || {}) {
		codeCount += Object.keys(projectFilter.activityCodes[typeId] || {}).length;
	}
	if (codeCount) {
		parts.push(`${codeCount} code${codeCount === 1 ? '' : 's'}`);
	}
	const resourceCount = Object.keys(projectFilter.resources || {}).length;
	if (resourceCount) {
		parts.push(`${resourceCount} resource${resourceCount === 1 ? '' : 's'}`);
	}
	if (projectFilter.applyFloatFilter) {
		parts.push('float');
	}
	if (projectFilter.startDate || projectFilter.endDate || (projectFilter.useDynamicDates && projectFilter.dynamicRange)) {
		parts.push('dates');
	}
	if (projectFilter.hasPhotos) {
		parts.push('photos');
	}
	if (projectFilter.changes) {
		parts.push('changes');
	}
	if (Object.keys(projectFilter.flags || {}).length) {
		parts.push('flags');
	}
	return parts.length ? parts.join(', ') : 'No filters';
};
